import React, { useEffect } from 'react';
import { Skeleton } from 'antd';
import ImageWrapper from '@/components/ImageWrapper/ImageWrapper';
import { currencyFormat } from '@/utilities/CommonUtils';
import './Summary.scss';
import ShipType from '@/components/ShipType/ShipType';
import { INVOICE_CUSTOM_ITEM_SVG } from '@/constants/Invoice';
import { useAppSelector } from '@/store/hooks';
import { Text } from '@/components/Text/Text';
import PromoCodeInput from '@/components/PromoCodeInput/PromoCodeInput';
import Icon from '@/components/Icon/Icon';
import EconomySavedShippingPill from '@/components/EconomySavedShippingPill/EconomySavedShippingPill';
import { ECONOMY_SHIPPING_KEY } from '@/constants/ShipTypes';
import { getShippingPrice } from '@/utilities/InvoiceUtils';
import type {
  ISummaryBreakdown,
  ISummaryItemProps,
  ISummaryProps,
} from '@/common/interfaces/invoice.interface';
import useViewport from '@/common/context/ViewPort/useViewPort';
import { PROMO_CODE_TYPES, SUBTOTAL_PROMO_CODE_ERROR_PREFIX } from '@/constants/Common';

const TabularSummaryItem: React.FC<{ item: ISummaryItemProps }> = ({ item }) => (
  <tr className={`summary-item-wrapper ${item.isItemRejected ? 'rejected-item' : ''}`}>
    <td className="design-info-td">
      <div className="design-information">
        <ImageWrapper
          size="small"
          imgUrl={item.imageSrc || '/src/assets/svg/image_error.svg'}
          fallbackSrc="/src/assets/svg/image_error.svg"
          className={`image-thumb ${
            item.imageSrc === INVOICE_CUSTOM_ITEM_SVG ? 'custom-item' : ''
          }`}
        />
        {item.description ? (
          <div className="style-code" title={item.description}>
            {item.description}
          </div>
        ) : (
          <div className="style-code" title={item.styleCode ? `#${item.styleCode}` : item.brand}>
            {item.styleCode ? `#${item.styleCode}` : item.brand}
          </div>
        )}
      </div>
    </td>
    <td className="text-center quantity-td">{item.quantity}</td>
    <td className="text-center price-td">
      <div className="price-wrapper">
        {!!item.discountedPrice && (
          <div className="striked-price">{currencyFormat(item.discountedPrice)}</div>
        )}
        <div className="price">{currencyFormat(item.pricePerUnit)}</div>
      </div>
    </td>
    <td className="text-right total-price-td">{currencyFormat(item.totalPrice)}</td>
  </tr>
);

const NonTabularSummaryItem: React.FC<{ item: ISummaryItemProps; isLoading: boolean }> = ({
  item,
  isLoading,
}) => (
  <div className={`item-wrapper ${item.isItemRejected ? 'rejected-item' : ''}`}>
    <div className="design-image-price-wrapper">
      <div className="design-image">
        {item.imageSrc ? (
          <ImageWrapper
            size="small"
            imgUrl={item.imageSrc}
            fallbackSrc="/src/assets/svg/image_error.svg"
            className={`image-thumb ${
              item.imageSrc === INVOICE_CUSTOM_ITEM_SVG ? 'custom-item' : ''
            }`}
          />
        ) : (
          <ImageWrapper
            size="small"
            imgUrl="/src/assets/svg/image_error.svg"
            className="image-thumb-placeholder"
          />
        )}
      </div>

      <div className="design-information">
        {item.description ? (
          <div className="style-code" title={item.description}>
            {item.description}
          </div>
        ) : (
          <div className="style-code" title={item.styleCode ? `#${item.styleCode}` : item.brand}>
            {item.styleCode ? `#${item.styleCode}` : item.brand}
          </div>
        )}

        <div className="pricing-information">
          <div className="quantity-price-info">
            <div className="quantity">{item.quantity}</div>
            <div className="cross">x</div>
            {!!item.discountedPrice && (
              <div className="striked-price">
                {!isLoading && <div>{currencyFormat(item.discountedPrice)}</div>}
                {isLoading && (
                  <Skeleton.Button style={{ width: 50, height: 20 }} active size="small" block />
                )}
              </div>
            )}
            <div className="price">
              {!isLoading && <div>{currencyFormat(item.pricePerUnit)}</div>}
              {isLoading && (
                <Skeleton.Button style={{ width: 50, height: 20 }} active size="small" block />
              )}
            </div>
          </div>

          <div className="design-item-total">
            {!isLoading && <div>{currencyFormat(item.totalPrice)}</div>}
            {isLoading && (
              <Skeleton.Button style={{ width: 50, height: 20 }} active size="small" block />
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SummaryBreakdown: React.FC<ISummaryBreakdown> = ({
  parentClass,
  subTotal,
  additionalCharges,
  additionalDiscount,
  salesTax,
  shipping,
  shipTypeKey,
  isLoading,
  showConfetti,
  showSalesTax,
  discountedTotal,
  total,
  isItemRejected,
  renderPromoCodeInput,
  renderDiscountLine,
  paymentReceived,
  showPaidInvoiceView,
  showGuarantee,
}) => {
  const shouldShowStrikedTotal =
    !!discountedTotal &&
    discountedTotal > 0 &&
    Number(discountedTotal).toFixed(2) !== Number(total).toFixed(2) &&
    !isLoading &&
    !isItemRejected;

  return (
    <div className={parentClass}>
      <div className="divider-dashed" />

      {renderPromoCodeInput && <div className="promocode-ip">{renderPromoCodeInput}</div>}

      <div className="shipping-breakdown">
        {/* SUBTOTAL SECTION */}
        <div className="sub-total d-flex">
          <div className="sub-total-title breakdown-t">Subtotal</div>
          <div className="sub-total-cost breakdown-c">
            {!isLoading && <div>{isItemRejected ? '- -' : currencyFormat(subTotal)}</div>}
            {isLoading && (
              <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
            )}
          </div>
        </div>

        {/* ADDITIONAL CHARGES SECTION */}
        {additionalCharges?.map((charge) => (
          <div key={charge.label} className="additional-charges d-flex">
            <div className="additional-charges-title breakdown-t">{charge.label}</div>
            <div className="additional-charges-cost breakdown-c">
              {!isLoading && <div>{currencyFormat(charge.value)}</div>}
              {isLoading && (
                <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
              )}
            </div>
          </div>
        ))}

        {showConfetti && (
          <img src="/src/assets/gif/confetti.gif" alt="confetti" className="confetti-gif" />
        )}
        {renderDiscountLine}

        {/* DISCOUNT SECTION */}
        {additionalDiscount?.map((discount, index) => (
          <div key={discount.id ?? index} className="discount d-flex">
            <div className="discount-label-container">
              {discount.id && (
                <Icon
                  height="20px"
                  width="20px"
                  source="/src/assets/svg/discount.svg"
                  className="discount-icon-active"
                />
              )}
              <div className="discount-title breakdown-t">{discount.label}</div>
            </div>
            <div className="discount-cost breakdown-c">
              {isLoading && (
                <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
              )}
              {!isLoading && <div>-{currencyFormat(discount.value)}</div>}
            </div>
          </div>
        ))}

        {/* SHIPPING SECTION */}
        <div className="shipping-total d-flex">
          <div className="stotal-title breakdown-t">
            <div>Shipping</div>
            {shipTypeKey && <ShipType shipType={shipTypeKey} />}
          </div>
          <div className="stotal-cost breakdown-c">
            {isLoading && (
              <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
            )}
            {!isLoading && (
              <div>
                {shipTypeKey === ECONOMY_SHIPPING_KEY && Number(shipping) < 0 ? (
                  <EconomySavedShippingPill shipping={shipping} />
                ) : (
                  getShippingPrice(shipTypeKey, shipping)
                )}
              </div>
            )}
          </div>
        </div>

        {/* GUARANTEE SECTION */}
        {showGuarantee !== '0' && (
          <div className="sales-tax d-flex">
            <div className="sales-tax-title breakdown-t">Guarantee</div>
            <div className="sales-tax-cost breakdown-c">
              {!isLoading && (
                <div>
                  {showGuarantee === 'FREE' ? 'FREE' : currencyFormat(Number(showGuarantee))}
                </div>
              )}
              {isLoading && (
                <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
              )}
            </div>
          </div>
        )}

        {/* SALES TAX SECTION */}
        {showSalesTax && (
          <div className="sales-tax d-flex">
            <div className="sales-tax-title breakdown-t">Sales Tax</div>
            {salesTax > 0 && (
              <div className="sales-tax-cost breakdown-c">
                {!isLoading && <div>{currencyFormat(salesTax)}</div>}
                {isLoading && (
                  <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
                )}
              </div>
            )}
            {!salesTax && (
              <div className="sales-tax-cost breakdown-c">
                {!isLoading && <div>TBD</div>}
                {isLoading && (
                  <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
                )}
              </div>
            )}
          </div>
        )}

        {/* PAYMENT EXPECTED SECTION */}
        {showPaidInvoiceView && (
          <div className="total-paid d-flex">
            <div className="total-paid-title breakdown-t">Final Price</div>
            <div className="total-paid-cost breakdown-c">
              {!isLoading && <div>{isItemRejected ? '- -' : currencyFormat(total ?? 0)}</div>}
              {isLoading && (
                <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
              )}
            </div>
          </div>
        )}

        {/* PAYMENT RECEIVED SECTION */}
        {showPaidInvoiceView && (
          <div className="total-paid d-flex">
            <div className="total-paid-title breakdown-t">Total Paid</div>
            <div className="total-paid-cost breakdown-c">
              {!isLoading && (
                <div>{isItemRejected ? '- -' : currencyFormat(paymentReceived ?? 0)}</div>
              )}
              {isLoading && (
                <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
              )}
            </div>
          </div>
        )}
      </div>

      <div className="divider-dashed" />

      <div className="total-section">
        <div className="total-title">{showPaidInvoiceView ? 'Total Due' : 'Order Total'}</div>
        {!showPaidInvoiceView && (
          <div className="total-cost">
            {shouldShowStrikedTotal && (
              <div className="striked-total">{currencyFormat(discountedTotal)}</div>
            )}
            {!isLoading && (
              <div className="og-cost">{isItemRejected ? '- -' : currencyFormat(total)}</div>
            )}
            {isLoading && (
              <Skeleton.Input style={{ width: 50, height: 20 }} active size="small" block />
            )}
          </div>
        )}
        {showPaidInvoiceView && (
          <div className="total-cost">
            {/* commenting out as per ENGG-37003 */}
            {/* {!!discountedTotal && discountedTotal > 0 && !isLoading && !isItemRejected && (
            <div className="striked-total">{currencyFormat(discountedTotal)}</div>
          )} */}
            <div className="og-cost">{currencyFormat(Number(total) - Number(paymentReceived))}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const Summary: React.FC<ISummaryProps> = ({
  summaryItems,
  salesTax,
  shipping,
  subTotal,
  total,
  discountedTotal,
  shipTypeKey,
  className,
  additionalDiscount,
  additionalCharges,
  showSalesTax = true,
  isItemRejected = false,
  promoFocus = false,
  promoCodeData,
  promoApplied = false,
  promoCodeError = '',
  setPromoCodeError,
  onPromoApplyClick,
  onPromoRemoveClick,
  showConfetti,
  summaryFormControl,
  paymentReceived,
  showPaidInvoiceView = false,
  page,
  showGuarantee,
}) => {
  const screen = useViewport(true);
  const styleClass = `summary--${screen}`;
  const { isLoading } = useAppSelector((state) => state.cart);
  const showPromoCodeInput = +subTotal > 0;
  const [renderInvoicePaidView, setRenderInvoicePaidView] = React.useState(false);
  const isSubtotalErrorMessage = Boolean(
    promoCodeError && promoCodeError.includes(SUBTOTAL_PROMO_CODE_ERROR_PREFIX)
  );

  useEffect(() => {
    if (showPaidInvoiceView && page === 'invoice') {
      setRenderInvoicePaidView(true);
    }
  }, [page, showPaidInvoiceView]);

  /** This renders the discount line item when promo code is applied */
  const renderDiscountLine = () => {
    if (!promoCodeData || promoCodeError || !promoApplied || !showPromoCodeInput) return null;
    if (promoCodeData.type === PROMO_CODE_TYPES.noGuaranteeFee) {
      return null;
    }

    return (
      <div className="promo-discount">
        <Text as="span" className="summary-label" size={14} color="#8C90AA" weight="medium">
          {promoCodeData.code}
        </Text>
        <Text as="span" className="summary-value" size={14} color="#37B996" weight="semibold">
          - ${promoCodeData.discount}
        </Text>
      </div>
    );
  };

  /** This renders the promo code input box */
  const renderPromoCodeInput = () => {
    if (
      !showPromoCodeInput ||
      !summaryFormControl ||
      !setPromoCodeError ||
      !onPromoApplyClick ||
      !onPromoRemoveClick
    ) {
      return null;
    }

    return (
      <PromoCodeInput
        promoFocus={promoFocus}
        promoCodeData={promoCodeData}
        promoApplied={promoApplied}
        promoCodeError={promoCodeError}
        setPromoCodeError={setPromoCodeError}
        onPromoApplyClick={onPromoApplyClick}
        onPromoRemoveClick={onPromoRemoveClick}
        control={summaryFormControl}
        isSubtotalErrorMessage={isSubtotalErrorMessage}
      />
    );
  };

  return (
    <div className={`${className ?? ''} ${styleClass}`}>
      {renderInvoicePaidView && (
        <div className="summary-table-container">
          {screen !== 'mobile' && (
            <table className="summary-responsive-table">
              <thead>
                <tr>
                  <th className="text-left">Product Name</th>
                  <th className="text-center">Qty</th>
                  <th className="text-center">Price</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {summaryItems.map((item, index) => (
                  <TabularSummaryItem key={`${item.styleCode}${index}`} item={item} />
                ))}
              </tbody>
            </table>
          )}

          {screen === 'mobile' && (
            <>
              {summaryItems.map((item, index) => (
                <NonTabularSummaryItem
                  key={`${item.styleCode}${index}`}
                  item={item}
                  isLoading={isLoading}
                />
              ))}
            </>
          )}

          <SummaryBreakdown
            parentClass="paid-summary-section"
            subTotal={subTotal}
            additionalCharges={additionalCharges}
            additionalDiscount={additionalDiscount}
            salesTax={salesTax}
            shipping={shipping}
            shipTypeKey={shipTypeKey}
            isLoading={isLoading}
            showConfetti={showConfetti}
            showSalesTax={showSalesTax}
            discountedTotal={discountedTotal}
            total={total}
            isItemRejected={isItemRejected}
            renderDiscountLine={renderDiscountLine()}
            paymentReceived={paymentReceived}
            showPaidInvoiceView={renderInvoicePaidView}
            showGuarantee={showGuarantee}
          />
        </div>
      )}
      {!renderInvoicePaidView && (
        <>
          <div className="summary-items">
            {summaryItems.map((item, index) => (
              <NonTabularSummaryItem
                key={`${item.styleCode}${index}`}
                item={item}
                isLoading={isLoading}
              />
            ))}
          </div>

          <SummaryBreakdown
            parentClass="summary-section"
            subTotal={subTotal}
            additionalCharges={additionalCharges}
            additionalDiscount={additionalDiscount}
            salesTax={salesTax}
            shipping={shipping}
            shipTypeKey={shipTypeKey}
            isLoading={isLoading}
            showConfetti={showConfetti}
            showSalesTax={showSalesTax}
            discountedTotal={discountedTotal}
            total={total}
            isItemRejected={isItemRejected}
            renderPromoCodeInput={renderPromoCodeInput()}
            renderDiscountLine={renderDiscountLine()}
            showGuarantee={showGuarantee}
          />
        </>
      )}
    </div>
  );
};

Summary.displayName = 'Summary';

export default Summary;
